import { CommonModule } from '@angular/common';
import { Component, computed, Inject, OnInit, signal } from '@angular/core';
import { Subscription } from 'rxjs';

import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MarkdownModule } from 'ngx-markdown';

import Resources from '@constants/resourses';
import { IBanner } from 'src/app/core/models/tenancy.interface';
import { BannerPlacement } from 'src/app/core/enums/banner.enum';

import { SupplierTariffComponent } from '@shared/components/quotes-result/components/supplier-tariff/supplier-tariff.component';

import { UtilityService } from '@services/utility.service';
import { SessionService } from '@services/session.service';
import { IQuotesTariff } from 'src/app/core/models/quotesTariff.interface';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { SpotlightTrackingService } from '@services/spotlight-tracking.service';
@Component({
  selector: 'app-tariff-details-dialog',
  standalone: true,
  imports: [CommonModule, MatTabsModule, MatIconModule, MatExpansionModule, MarkdownModule, MatDialogModule, SupplierTariffComponent],
  templateUrl: './tariff-details-dialog.component.html',
  styleUrl: './tariff-details-dialog.component.scss'
})
export class TariffDetailsDialogComponent implements OnInit {
  subscription: Subscription[] = [];
  id: string = '';
  currentSupplier: any = this.sessionService.getData(Resources.SessionStorageKeys.QuotesData)?.CurrentSupplier;

  isTablet: boolean = window.innerWidth <= 1024 && window.innerWidth > 767;

  elecCurrentUsageAnnually: any = signal(0);
  elecCurrentUsageMonthly: any = computed(() => this.utilityService.formatNumber(this.elecCurrentUsageAnnually() / 12));

  elecSavingPerYear: any = signal(0);
  elecSavingPerMonth: any = computed(() => this.utilityService.formatNumber(this.elecSavingPerYear() / 12));

  gasCurrentUsageAnnually: any = signal(0);
  gasCurrentUsageMonthly: any = computed(() => this.utilityService.formatNumber(this.gasCurrentUsageAnnually() / 12));

  gasSavingPerYear: any = signal(0);
  gasSavingPerMonth: any = computed(() => this.utilityService.formatNumber(this.gasSavingPerYear() / 12));

  isNegativeNumber: any = (num) => this.utilityService.isNegativeNumber(num);

  content: any = {
    savingCalculated: '',
    claimRewards: {
      title: '',
      content: ''
    }
  };

  tenancyBanner: IBanner[];
  smartMeterInfo: any = 'N/A';
  tariffDetailsRewardsBanners: IBanner[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<TariffDetailsDialogComponent>,
    private utilityService: UtilityService,
    private sessionService: SessionService,
    private googleAnalytics: GoogleAnalyticsService,
    private spotlightTracking: SpotlightTrackingService
  ) {
    this.id = 'TariffInformation';

    this.elecCurrentUsageAnnually.set(Number(this.currentSupplier?.Fuels?.Electricity?.AnnualConsumption));
    this.gasCurrentUsageAnnually.set(Number(this.currentSupplier?.Fuels?.Gas?.AnnualConsumption));

    this.elecSavingPerYear.set(Math.abs(Number(this.data?.selectedQuote?.Fuels?.Electricity?.SavingsPerYear)));
    this.gasSavingPerYear.set(Math.abs(Number(this.data?.selectedQuote?.Fuels?.Gas?.SavingsPerYear)));

    this.subscription.push(
      this.utilityService.getMDFile('quotesPage/quotePage-tariffDetails-savingCalculated.md').subscribe((res) => {
        this.content.savingCalculated = res;
      })
    );

    this.subscription.push(
      this.utilityService.getMDFile('quotesPage/quotePage-tariffDetails-claimRewards.md').subscribe((res) => {
        const splitData: any[] = res.split('\n\n');
        this.content.claimRewards['title'] = splitData[0];
        this.content.claimRewards['content'] = splitData[1];
      })
    );
  }

  // Whether a banner has opted the current tariff's supplier out via excludeSuppliers
  private isSupplierExcluded(banner: IBanner): boolean {
    if (!banner.excludeSuppliers || banner.excludeSuppliers.length === 0) {
      return false;
    }

    return banner.excludeSuppliers.includes(this.data?.selectedQuote?.SupplierName);
  }

  ngOnInit(): void {
    // Set initial value if not already set
    if (this.data && !this.data.isSmallScreen) {
      this.data.isSmallScreen = window.innerWidth <= 767;
    }

    // Get tenancy banner
    this.tenancyBanner = (this.data?.banner && this.data?.banner?.length > 0) ? this.data.banner : [];

    // If available banner then sort by version 2
    if (this.tenancyBanner?.length > 0) {
      this.tenancyBanner = this.tenancyBanner
        .filter((item: IBanner) => item.version === 2)
        .sort((a: IBanner, b: IBanner) => (a.priority || 0) - (b.priority || 0));

      const smartMeterBanners = this.tenancyBanner.filter(
        (banner: IBanner) => banner.placetoDisplay === BannerPlacement.TariffDetailsSmartMeter
      );

      const matchedBanner = smartMeterBanners.find(banner =>
        this.utilityService.findQuoteByFilterKey([this.data.selectedQuote], banner.dynamicContentUniqueKey).length > 0
      );

      this.smartMeterInfo = matchedBanner?.content ?? 'N/A';

      // Get the reward info banners (icon + content + description) for the tariff details page
      this.tariffDetailsRewardsBanners = this.tenancyBanner.filter(
        (banner: IBanner) =>
          banner.placetoDisplay === BannerPlacement.TariffDetailsRewards &&
          banner.status === 'active' &&
          this.utilityService.findQuoteByFilterKey([this.data.selectedQuote], banner.dynamicContentUniqueKey).length > 0 &&
          !this.isSupplierExcluded(banner)
      );
    }
  }

  // Method to handle the click event of the "Switch Now" button
  switchNow(selectedSwitchDetails: IQuotesTariff): void {
    const isSpotlightVariantActive = this.utilityService.getCookie(Resources.ABtestingKeys.TOP_SPOTLIGHT_VARIANT_COOKIE) === 'B';
    const clickedSpotlights = this.spotlightTracking.getClickedSpotlights(selectedSwitchDetails?.TariffName);
    this.googleAnalytics.track({
      from: 'tariff_details_dialog',
      supplier_name: selectedSwitchDetails?.SupplierName,
      tariff_name: selectedSwitchDetails?.TariffName,
      ...(isSpotlightVariantActive && clickedSpotlights.length ? { spotlight_clicked: clickedSpotlights } : {})
    }, 'switch_now');
    // Close the dialog and pass the selected switch details
    this.dialogRef.close(selectedSwitchDetails);
  }

  // Method to close the dialog
  closeDialog(): void {
    this.dialogRef.close();
  }
}